import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type { Role, UserRow } from "@/lib/types";

export interface UserInput {
  email?: string;        // create only — backend ignores on PATCH (read_only after creation)
  first_name?: string;
  last_name?: string;
  role?: Role;
  department_id?: string | null;
  /** CEO-only on create with role="user": the Director the new user
   *  reports to. The backend writes this onto `created_by`. */
  manager_id?: string | null;
  /** Multi-select: the departments this CEO leads. Only meaningful
   *  when role === "ceo". Backend rejects depts already led by
   *  someone else. */
  led_department_ids?: string[];
  position?: string;
  phone?: string;
}

/** POST /api/v1/users/ — invite flow. Backend creates with unusable
 *  password, sends an invite email with a set-password link, returns
 *  the new UserRow. Role gets clamped server-side to what the actor
 *  is allowed to assign (Director→user, CEO→user/director, Admin→any). */
export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UserInput) => {
      const { data } = await api.post<UserRow>("/users/", input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

/** PATCH /api/v1/users/<id>/. Email is read-only after creation. */
export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UserInput }) => {
      const { data } = await api.patch<UserRow>(`/users/${id}/`, input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

/** DELETE /api/v1/users/<id>/ — soft delete (flips is_active=false). */
export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/users/${id}/`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

/** POST /users/<id>/resend-invite/ — mints a new set-password link
 *  and emails it. Backend refuses for deactivated accounts. */
export function useResendInvite() {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<{ detail: string }>(`/users/${id}/resend-invite/`);
      return data;
    },
  });
}

// ---------------------------------------------------------------------------
// Departments — small, no pagination, used in the user form dropdown.
// ---------------------------------------------------------------------------

export interface Department {
  id: string;
  name: string;
  description: string;
  /** Annotated by the viewset; absent when used outside the list. */
  member_count?: number;
  /** The CEO leading this department (null if unassigned). One per dept. */
  ceo_id?: string | null;
  ceo_name?: string | null;
}

export function useDepartments() {
  return useQuery<Department[]>({
    queryKey: ["departments"],
    queryFn: async () => {
      const { data } = await api.get<
        { results: Department[] } | Department[]
      >("/departments/");
      // DRF returns a paginated envelope when PAGE_SIZE is set globally;
      // unwrap if needed.
      return Array.isArray(data) ? data : data.results;
    },
    staleTime: 5 * 60_000,
  });
}

// Department mutations — CEO+ only on the backend; we don't enforce that
// client-side beyond hiding the management page from non-CEO roles in
// the sidebar. The API will return 403 to anyone else.

export interface DepartmentInput {
  name: string;
  description?: string;
  /** Admin-only: the CEO leading this dept. Null clears the assignment. */
  ceo_id?: string | null;
}

export function useCreateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DepartmentInput) => {
      const { data } = await api.post<Department>("/departments/", input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["departments"] }),
  });
}

export function useUpdateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: DepartmentInput }) => {
      const { data } = await api.patch<Department>(`/departments/${id}/`, input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["departments"] }),
  });
}

export function useDeleteDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/departments/${id}/`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["departments"] }),
  });
}
